import { missionControlCodec } from "@/navigation/apps/missionControlLocation";
import type { EntityPreviewEntityType } from "@/components/chat/embeds/types";
import {
  AGENT_ENTITY_DESCRIPTORS,
  getAgentEntityDescriptor,
  getAppIdForEntityKind,
  isFeOpenAppIdAllowed,
} from "./catalog";
import {
  LIST_FOCUS_DETAIL_PARAM_KEYS,
  type AgentEntityKind,
  type AgentNavIntent,
  type AgentNavResolved,
  type AgentOpenWindowTarget,
} from "./types";

export function buildEntityFocusKey(entityType: EntityPreviewEntityType, id: string): string {
  return `${entityType}:${id}`;
}

export function stripDetailParamsForListFocus(params: Record<string, unknown>): Record<string, unknown> {
  if (!params.focus) {
    return params;
  }
  const out = { ...params };
  for (const key of LIST_FOCUS_DETAIL_PARAM_KEYS as readonly string[]) {
    delete out[key];
  }
  return out;
}

export function normalizeWindowParamsForApp(
  appId: string,
  rawParams: Record<string, unknown> | undefined,
): Record<string, string> {
  if (!rawParams) {
    return {};
  }
  let windowParams = stripDetailParamsForListFocus(rawParams);
  const focusKey = String(windowParams.focus ?? "").trim();
  if (focusKey.startsWith("task:") && appId === "mission-control" && !windowParams.view) {
    windowParams = { ...windowParams, view: "tasks" };
  }
  if (focusKey.startsWith("mission:")) {
    if (!windowParams.visibility) {
      windowParams = { ...windowParams, visibility: "all" };
    }
    if (appId === "mission-control" && !windowParams.view) {
      windowParams = { ...windowParams, view: "missions" };
    }
  }
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(windowParams)) {
    if (value !== undefined && value !== null) {
      result[key] = String(value);
    }
  }
  if (appId === "mission-control") {
    return missionControlCodec.toParams(missionControlCodec.fromParams(result));
  }
  if ((appId === "portfolio" || appId === "product") && !result.id) {
    const resourceId = String(
      windowParams.portfolio_id ??
        windowParams.portfolioId ??
        windowParams.product_id ??
        windowParams.productId ??
        "",
    ).trim();
    if (resourceId) {
      result.id = resourceId;
    }
  }
  return result;
}

const DETAIL_ID_ALIASES: Record<string, readonly string[]> = {
  missionId: ["missionId", "mission_id"],
  taskId: ["taskId", "task_id", "task"],
  caseId: ["caseId", "case_id"],
  meetingId: ["meetingId", "meeting_id"],
  faClientId: ["faClientId", "fa_client_id", "clientId", "client_id"],
  id: ["id", "artifactId", "artifact_id", "portfolioId", "portfolio_id", "productId", "product_id"],
};

function readDetailEntityId(paramKey: string, params: Record<string, string>): string {
  for (const alias of DETAIL_ID_ALIASES[paramKey] ?? [paramKey]) {
    const value = String(params[alias] ?? "").trim();
    if (value) {
      return value;
    }
  }
  return "";
}

/**
 * When an app opens with a catalog detail id (and no list ``focus``), route via executeNavIntent
 * so window params and classic URLs match location modules.
 */
export function catalogDetailNavIntent(
  appId: string,
  params: Record<string, string>,
): AgentNavIntent | null {
  if (String(params.focus ?? "").trim()) {
    return null;
  }

  if (appId === "mission-control") {
    const taskId = readDetailEntityId("taskId", params);
    if (taskId) {
      return { mode: "detail", entityKind: "task", id: taskId };
    }
    const missionId = readDetailEntityId("missionId", params);
    if (missionId) {
      return { mode: "detail", entityKind: "mission", id: missionId };
    }
    return null;
  }

  for (const descriptor of AGENT_ENTITY_DESCRIPTORS) {
    const detail = descriptor.detail;
    if (!detail) {
      continue;
    }
    const detailAppId = detail.detailAppId ?? descriptor.appId;
    if (appId !== descriptor.appId && appId !== detailAppId) {
      continue;
    }
    const id = readDetailEntityId(detail.windowParamKey, params);
    if (id) {
      return { mode: "detail", entityKind: descriptor.kind, id };
    }
  }
  return null;
}

/** @deprecated Use catalogDetailNavIntent — kept for existing tests and callers. */
export function missionControlDetailNavIntent(
  appId: string,
  params: Record<string, string>,
): AgentNavIntent | null {
  if (appId !== "mission-control") {
    return null;
  }
  return catalogDetailNavIntent(appId, params);
}

export function resolveNavIntent(intent: AgentNavIntent, titleForApp?: string): AgentNavResolved | null {
  if (intent.mode === "artifact") {
    return {
      kind: "window",
      target: {
        appId: "artifact",
        title: intent.title ?? "Letter",
        params: { id: intent.artifactId },
      },
    };
  }

  if (intent.mode === "app") {
    const params = intent.params ?? {};
    const normalized = normalizeWindowParamsForApp(intent.appId, params);
    return {
      kind: "window",
      target: {
        appId: intent.appId,
        title: titleForApp ?? intent.appId,
        params: normalized,
      },
    };
  }

  const descriptor = getAgentEntityDescriptor(intent.entityKind);
  if (!descriptor) {
    return null;
  }

  if (intent.mode === "list_highlight") {
    const list = descriptor.listHighlight;
    if (!list) {
      return null;
    }
    return {
      kind: "window",
      target: {
        appId: descriptor.appId,
        title: titleForApp ?? descriptor.listWindowTitle,
        params: list.windowParams(intent.id),
      },
    };
  }

  const detail = descriptor.detail;
  if (!detail) {
    return null;
  }
  const label = intent.label ?? intent.id;
  return {
    kind: "window",
    target: {
      appId: detail.detailAppId ?? descriptor.appId,
      title: detail.windowTitle?.(label) ?? label,
      params: {
        ...(detail.extraWindowParams ?? {}),
        [detail.windowParamKey]: intent.id,
      },
    },
  };
}

export function resolveListHighlightHref(
  entityKind: AgentEntityKind,
  id: string,
): { path: string; search: string } | null {
  const descriptor = getAgentEntityDescriptor(entityKind);
  const list = descriptor?.listHighlight;
  if (!list) {
    return null;
  }
  const query = list.routeQuery(id);
  const search = new URLSearchParams(query).toString();
  return {
    path: list.routePath,
    search: search ? `?${search}` : "",
  };
}

export function isEntityKindAllowedForProfile(
  entityKind: AgentEntityKind,
  allowedOpenAppIds: readonly string[] | undefined,
): boolean {
  if (!allowedOpenAppIds) {
    return true;
  }
  const descriptor = getAgentEntityDescriptor(entityKind);
  if (!descriptor) {
    return false;
  }
  if (isFeOpenAppIdAllowed(descriptor.appId, allowedOpenAppIds)) {
    return true;
  }
  const detailAppId = descriptor.detail?.detailAppId;
  return detailAppId ? isFeOpenAppIdAllowed(detailAppId, allowedOpenAppIds) : false;
}

export type AgentNavigatorDeps = {
  openWindow: (target: AgentOpenWindowTarget) => void;
  navigate: (path: string, query?: Record<string, string>) => void;
  inOsLayout: boolean;
  titleForApp?: (appId: string) => string;
};

function appIdForNavIntent(intent: AgentNavIntent): string {
  if (intent.mode === "app") {
    return intent.appId;
  }
  if (intent.mode === "artifact") {
    return "artifact";
  }
  return getAppIdForEntityKind(intent.entityKind) ?? "";
}

export function executeNavIntent(intent: AgentNavIntent, deps: AgentNavigatorDeps): boolean {
  const titleForApp = deps.titleForApp?.(appIdForNavIntent(intent)) ?? undefined;
  const resolved = resolveNavIntent(intent, titleForApp);
  if (!resolved) {
    return false;
  }

  if (resolved.kind === "href") {
    const query: Record<string, string> = {};
    if (resolved.target.search.startsWith("?")) {
      const params = new URLSearchParams(resolved.target.search.slice(1));
      params.forEach((value, key) => {
        query[key] = value;
      });
    }
    deps.navigate(resolved.target.path, Object.keys(query).length > 0 ? query : undefined);
    return true;
  }

  if (!deps.inOsLayout && intent.mode === "list_highlight") {
    const href = resolveListHighlightHref(intent.entityKind, intent.id);
    if (href) {
      const query: Record<string, string> = {};
      const params = new URLSearchParams(href.search.replace(/^\?/, ""));
      params.forEach((value, key) => {
        query[key] = value;
      });
      deps.navigate(href.path, Object.keys(query).length > 0 ? query : undefined);
      return true;
    }
  }

  if (!deps.inOsLayout && intent.mode === "detail") {
    const descriptor = getAgentEntityDescriptor(intent.entityKind);
    const detail = descriptor?.detail;
    if (detail) {
      deps.navigate(detail.routePath(intent.id));
      return true;
    }
  }

  deps.openWindow(resolved.target);
  return true;
}
